import { App, Button, Empty, Flex } from "antd";
import dayjs, { Dayjs } from "dayjs";
import React, {
    FC,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    deleteInvoiceDeletePayments,
    getApiError,
    getDebtPointPayments,
    getExportExportPaymentsThunk,
    postInvoiceMakeExportPayment,
} from "tm-shared/api";
import { backendDateFormat } from "tm-shared/constants";
import { PointDebt } from "tm-shared/types";
import { formatUAHStandard } from "tm-shared/utils";

import {
    useAppDispatch,
    useAppSelector,
    useOfflineModeSelector,
    useProtectedUserRoleSelector,
} from "@/hooks/redux";
import ExportDropdown from "@/pages/SV/RouteManagement/components/ExportDropdown";
import { StyledSum } from "@/pages/WorkPlan/Tasks/shared";
import { AppDatePicker } from "@/shared/forms";
import { BlockLoader } from "@/shared/ui/BlockLoader";
import { PrimaryText } from "@/shared/ui/Typography";
import { setAsyncStatus } from "@/store/slices/ui";
import { isManagerRole } from "@/utils/business/isManagerRole";

import { PaymentHeader } from "./components/PaymentHeader";
import { PaymentItem } from "./components/PaymentItem";
import {
    StyledActionsContainer,
    StyledCollapse,
    StyledPaymentsContainer,
} from "./Styled";

// --- Props ---

interface PaymentsProps {
    routeId: string;
    employeeId: string;
}

// --- Payments screen ---

const Payments: FC<PaymentsProps> = ({ routeId, employeeId }) => {
    // --- App hooks ---

    const { notification } = App.useApp();
    const dispatch = useAppDispatch();

    // --- Store selectors ---

    const exportIndicator = useAppSelector(
        (state) => state.work.exportIndicator,
    );
    const paymentExportPending = useAppSelector(
        (state) => state.ui.asyncStatus.paymentExportPending,
    );
    const role = useProtectedUserRoleSelector();
    const offlineMode = useOfflineModeSelector();

    // --- Local state ---

    const [paymentExportLoading, setPaymentExportLoading] = useState(false);
    const [dataLoading, setDataLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [debtPaymentList, setDebtPaymentList] = useState<PointDebt[]>([]);
    const [targetDate, setTargetDate] = useState(() =>
        dayjs().format(backendDateFormat.date),
    );

    // --- Derived state ---

    const isToday = dayjs().isSame(targetDate, "day");
    const isManager = isManagerRole(role);

    const allPayments = useMemo(
        () =>
            Object.values(debtPaymentList).flatMap(({ payments }) => payments),
        [debtPaymentList],
    );

    const isAllOwnExported = useMemo(
        () =>
            allPayments.every(({ isExported, payer }) => {
                if (payer !== employeeId) return true;

                return isExported;
            }),
        [allPayments, employeeId],
    );

    const totalSum = useMemo(
        () =>
            allPayments.reduce(
                (sum, { paidAmount }) => sum + (paidAmount ?? 0),
                0,
            ),
        [allPayments],
    );

    // --- Refs ---

    const prevPaymentExportPendingRef = useRef(paymentExportPending);

    // --- Data actions ---

    const getDebtPaymentsLocal = useCallback(async () => {
        try {
            const { result } = await getDebtPointPayments({
                routeId,
                targetDate,
            });

            setDebtPaymentList(result);
        } catch (error) {
            const { displayMessage } = getApiError(error);

            notification.error({
                title: "Помилка при завантаженні оплат",
                description: displayMessage,
            });
        }
    }, [notification, routeId, targetDate]);

    const fetchDebtPayments = useCallback(async () => {
        setDataLoading(true);

        await getDebtPaymentsLocal();

        setDataLoading(false);
    }, [getDebtPaymentsLocal]);

    // --- UI actions ---

    const handleDateChange = (date: Dayjs | null) => {
        if (date) {
            setTargetDate(date.format(backendDateFormat.date));
        }
    };

    const handleDeletePayments = useCallback(
        async (paymentIds: string[]) => {
            try {
                setDeleteLoading(true);

                await deleteInvoiceDeletePayments({
                    paymentIds,
                    force: false,
                    payerId: employeeId,
                });
            } catch (error) {
                const { displayMessage } = getApiError(error);

                notification.error({
                    title: "Не вдалося видалити",
                    description: displayMessage,
                });
            } finally {
                await getDebtPaymentsLocal();

                setDeleteLoading(false);
            }
        },
        [employeeId, getDebtPaymentsLocal, notification],
    );

    const handleExportPayment = async () => {
        if (isAllOwnExported) {
            notification.error({
                title: "Всі оплати вивантажені, очікуйте на результат",
            });
            return;
        }

        try {
            setPaymentExportLoading(true);

            await postInvoiceMakeExportPayment({
                payerId: employeeId,
            });

            dispatch(
                setAsyncStatus({ key: "paymentExportPending", value: true }),
            );

            notification.success({
                title: "Запит на вивантаження оплат успішно відправлено",
            });
        } catch (error) {
            const { displayMessage } = getApiError(error);

            notification.error({
                title: "Помилка при вивантаженні оплат",
                description: displayMessage,
            });
        } finally {
            setPaymentExportLoading(false);
        }
    };

    const exportPaymentFile = useCallback(
        async ({ key }: { key: string }) => {
            if (!isToday) {
                notification.error({
                    title: "Експорт дозволений тільки на сьогоднішню дату",
                });
                return;
            }

            switch (key) {
                case "excel": {
                    if (employeeId) {
                        dispatch(
                            getExportExportPaymentsThunk({
                                payerId: employeeId,
                            }),
                        );
                        notification.info({
                            title: "Експорт виплат розпочато",
                        });
                    } else {
                        notification.error({
                            title: "Помилка при експорті виплат",
                            description:
                                "Не знайдено ідентифікатор посади працівника",
                        });
                    }

                    return;
                }

                default: {
                    return;
                }
            }
        },
        [notification, dispatch, employeeId, isToday],
    );

    // --- Effects ---

    useEffect(() => {
        fetchDebtPayments();
    }, [fetchDebtPayments, notification]);

    useEffect(() => {
        const prevPaymentExportPending = prevPaymentExportPendingRef.current;

        if (prevPaymentExportPending && !paymentExportPending) {
            fetchDebtPayments();
        }

        prevPaymentExportPendingRef.current = paymentExportPending;
    }, [paymentExportPending, fetchDebtPayments]);

    // --- Render data ---

    const collapseItems = Object.values(debtPaymentList).map((debt) => {
        return {
            key: debt.point.id,
            label: (
                <PaymentHeader
                    debt={debt}
                    onDeletePayments={handleDeletePayments}
                    deleteLoading={deleteLoading}
                    canDelete={isToday && !paymentExportPending}
                    employeeId={employeeId}
                    isManager={isManager}
                />
            ),
            children: (
                <Flex vertical gap={10}>
                    {debt.payments.map((payment) => (
                        <PaymentItem
                            key={payment.consignmentId}
                            payment={payment}
                            employeeId={employeeId}
                            deleteLoading={deleteLoading}
                            onDeletePayments={handleDeletePayments}
                            canDelete={isToday && !paymentExportPending}
                            isManager={isManager}
                        />
                    ))}
                </Flex>
            ),
        };
    });

    // --- Render ---

    return (
        <>
            <StyledActionsContainer>
                <ExportDropdown
                    isLoading={!!exportIndicator}
                    loadingLabel={exportIndicator.toString()}
                    menuItems={[
                        {
                            key: "excel",
                            label: "Експорт в Excel",
                            disabled: !isToday || dataLoading,
                        },
                    ]}
                    onMenuClick={exportPaymentFile}
                />

                <AppDatePicker
                    placeholder="Обрати дату"
                    value={dayjs(targetDate)}
                    format="DD MMMM YYYY"
                    onChange={handleDateChange}
                    allowClear={false}
                    disabledDate={(current) =>
                        current && current > dayjs().endOf("day")
                    }
                    size="large"
                    disabled={offlineMode || dataLoading}
                />
            </StyledActionsContainer>

            {dataLoading ? (
                <Flex
                    flex={1}
                    align="center"
                    justify="center"
                    style={{ marginTop: 24 }}
                >
                    <BlockLoader text="Завантаження оплат..." />
                </Flex>
            ) : (
                <>
                    {!paymentExportPending && isToday && !isAllOwnExported && (
                        <Button
                            type="primary"
                            onClick={handleExportPayment}
                            loading={paymentExportLoading}
                            style={{
                                width: "100%",
                                padding: "18px 0px",
                                marginBottom: 12,
                            }}
                        >
                            Вивантажити оплати
                        </Button>
                    )}

                    <StyledPaymentsContainer>
                        {!Object.keys(debtPaymentList).length ? (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description="Немає оплат"
                            />
                        ) : (
                            <>
                                <StyledSum>
                                    <PrimaryText textColor="white">
                                        Загальна сума:
                                    </PrimaryText>

                                    <PrimaryText
                                        textColor="white"
                                        fontWeight="600"
                                    >
                                        {formatUAHStandard(totalSum)}
                                    </PrimaryText>
                                </StyledSum>

                                <StyledCollapse
                                    accordion
                                    items={collapseItems}
                                />
                            </>
                        )}
                    </StyledPaymentsContainer>
                </>
            )}
        </>
    );
};

// --- Export ---

export default React.memo(Payments);
